if (!customElements.get('sooler-media-with-text')) {
  customElements.define('sooler-media-with-text', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.inlineVideo = this.querySelector('.sooler-mwt__video');
      if (!this.inlineVideo) return;
      this.initialized = true;
      this.media = this.querySelector('[data-sooler-mwt-media]');
      this.openButton = this.querySelector('[data-sooler-mwt-video-open]');
      this.soundButton = this.querySelector('[data-sooler-mwt-video-sound]');
      this.viewer = this.querySelector('[data-sooler-mwt-viewer]');
      this.viewerMedia = this.querySelector('[data-sooler-mwt-viewer-media]');
      this.viewerClose = this.querySelector('[data-sooler-mwt-viewer-close]');
      this.viewerPlay = this.querySelector('[data-sooler-mwt-viewer-play]');
      this.viewerSound = this.querySelector('[data-sooler-mwt-viewer-sound]');
      this.canExpand = this.dataset.videoExpand === 'true' && Boolean(this.viewer);
      this.viewerVideo = null;
      this.viewerMuted = true;

      this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.onInlineStateChange = this.updateInlineState.bind(this);
      this.onMediaClick = this.handleMediaClick.bind(this);
      this.onOpenClick = this.handleOpenClick.bind(this);
      this.onSoundClick = this.toggleInlineSound.bind(this);

      this.media.dataset.expand = String(this.canExpand);
      this.inlineVideo.addEventListener('play', this.onInlineStateChange);
      this.inlineVideo.addEventListener('pause', this.onInlineStateChange);
      this.media.addEventListener('click', this.onMediaClick);
      this.openButton?.addEventListener('click', this.onOpenClick);
      this.soundButton?.addEventListener('click', this.onSoundClick);
      this.viewerClose?.addEventListener('click', () => this.closeViewer());
      this.viewerPlay?.addEventListener('click', () => this.toggleViewerPlayback());
      this.viewerSound?.addEventListener('click', () => this.toggleViewerSound());
      this.viewer?.addEventListener('click', (event) => {
        if (event.target === this.viewer) this.closeViewer();
      });
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.updateInlineState();

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.inlineVideo.pause();
      else if (this.inlineVideo.autoplay) this.inlineVideo.play()?.catch(() => this.updateInlineState());
    }

    disconnectedCallback() {
      this.inlineVideo?.removeEventListener('play', this.onInlineStateChange);
      this.inlineVideo?.removeEventListener('pause', this.onInlineStateChange);
      this.media?.removeEventListener('click', this.onMediaClick);
      this.openButton?.removeEventListener('click', this.onOpenClick);
      this.soundButton?.removeEventListener('click', this.onSoundClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      if (this.viewer && this.viewer.parentElement === document.body) this.appendChild(this.viewer);
      this.initialized = false;
    }

    updateInlineState() {
      const playing = !this.inlineVideo.paused && !this.inlineVideo.ended;
      this.media.classList.toggle('is-playing', playing);
      if (this.openButton) this.openButton.setAttribute('aria-label', this.canExpand ? 'Open video' : (playing ? 'Pause video' : 'Play video'));
    }

    handleMediaClick(event) {
      if (event.target.closest('button')) return;
      if (this.canExpand) this.openViewer();
      else this.toggleInlinePlayback();
    }

    handleOpenClick(event) {
      event.stopPropagation();
      if (this.canExpand) this.openViewer();
      else this.toggleInlinePlayback();
    }

    toggleInlinePlayback() {
      if (this.inlineVideo.paused) this.inlineVideo.play()?.catch(() => this.updateInlineState());
      else this.inlineVideo.pause();
    }

    toggleInlineSound(event) {
      event?.stopPropagation();
      this.inlineVideo.muted = !this.inlineVideo.muted;
      this.soundButton?.classList.toggle('is-unmuted', !this.inlineVideo.muted);
      this.soundButton?.setAttribute('aria-label', this.inlineVideo.muted ? 'Turn sound on' : 'Turn sound off');
    }

    buildViewer() {
      if (this.viewerVideo || !this.viewerMedia) return;
      this.viewerVideo = this.inlineVideo.cloneNode(true);
      this.viewerVideo.className = 'sooler-mwt__viewer-video';
      this.viewerVideo.removeAttribute('autoplay');
      this.viewerVideo.autoplay = false;
      this.viewerVideo.controls = false;
      this.viewerVideo.loop = true;
      this.viewerVideo.muted = true;
      this.viewerVideo.preload = 'metadata';
      this.viewerVideo.addEventListener('play', () => this.updateViewerState());
      this.viewerVideo.addEventListener('pause', () => this.updateViewerState());
      this.viewerVideo.addEventListener('click', () => this.toggleViewerPlayback());
      this.viewerMedia.appendChild(this.viewerVideo);
    }

    openViewer() {
      if (!this.viewer) return;
      this.buildViewer();
      this.lastFocusedElement = document.activeElement;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.viewer.hidden = false;
      this.viewer.setAttribute('aria-hidden', 'false');
      document.body.appendChild(this.viewer);
      this.viewerVideo.currentTime = this.inlineVideo.currentTime || 0;
      this.viewerVideo.muted = this.viewerMuted;
      this.viewerVideo.play()?.catch(() => this.updateViewerState());
      this.viewerClose?.focus();
      this.updateViewerState();
    }

    closeViewer() {
      if (!this.viewer || this.viewer.hidden) return;
      this.viewerVideo?.pause();
      this.viewer.hidden = true;
      this.viewer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = this.previousBodyOverflow || '';
      this.appendChild(this.viewer);
      this.lastFocusedElement?.focus?.();
    }

    toggleViewerPlayback() {
      if (!this.viewerVideo) return;
      if (this.viewerVideo.paused) this.viewerVideo.play()?.catch(() => this.updateViewerState());
      else this.viewerVideo.pause();
    }

    toggleViewerSound() {
      if (!this.viewerVideo) return;
      this.viewerMuted = !this.viewerMuted;
      this.viewerVideo.muted = this.viewerMuted;
      this.updateViewerState();
    }

    updateViewerState() {
      if (!this.viewerVideo) return;
      this.viewerPlay?.classList.toggle('is-paused', this.viewerVideo.paused);
      this.viewerPlay?.setAttribute('aria-label', this.viewerVideo.paused ? 'Play video' : 'Pause video');
      this.viewerSound?.classList.toggle('is-unmuted', !this.viewerMuted);
      this.viewerSound?.setAttribute('aria-label', this.viewerMuted ? 'Turn sound on' : 'Turn sound off');
    }

    handleDocumentKeydown(event) {
      if (!this.viewer || this.viewer.hidden) return;
      if (event.key === 'Escape') this.closeViewer();
    }
  });
}
